import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getApiBaseUrl } from "../config/api";

const STEPS = ["Enter Amount", "Share Payment Code", "Payment Confirmed"];
const POLL_MS = 8000;

const dialogPaperSx = {
    bgcolor: "#111418",
    color: "#f0ede8",
    backgroundImage: "none",
    border: "1px solid rgba(255,255,255,0.08)",
};

const goldButtonSx = {
    bgcolor: "#e8c97e",
    color: "#111418",
    fontWeight: 700,
    "&:hover": { bgcolor: "#d4b56a" },
};

function formatCountdown(totalSeconds) {
    const safe = Math.max(0, Math.floor(totalSeconds));
    const m = Math.floor(safe / 60);
    const s = safe % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function formatMwk(value) {
    return `MWK ${Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })}`;
}

function extractErrorMessage(error) {
    const data = error?.response?.data;
    if (typeof data === "string" && data.trim()) return data.trim();
    if (data?.error) return String(data.error);
    if (data?.message) return String(data.message);
    if (error?.code === "ERR_NETWORK" || error?.message === "Network Error") {
        return "Network error. Check your connection and try again.";
    }
    return error?.message || "Could not start online payment";
}

export default function OnekhusaPaymentDialog({
    open,
    onClose,
    onPaymentConfirmed,
    feeRecordId,
    studentName,
    outstandingBalance,
    schoolId,
}) {
    const [activeStep, setActiveStep] = useState(0);
    const [amount, setAmount] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [session, setSession] = useState(null);
    const [countdownSeconds, setCountdownSeconds] = useState(0);
    const [expired, setExpired] = useState(false);
    const [completedPayment, setCompletedPayment] = useState(null);

    const pollRef = useRef(null);
    const tickRef = useRef(null);

    const clearTimers = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    }, []);

    const resetDialog = useCallback(() => {
        clearTimers();
        setActiveStep(0);
        setAmount(String(outstandingBalance ?? ""));
        setLoading(false);
        setError("");
        setSession(null);
        setCountdownSeconds(0);
        setExpired(false);
        setCompletedPayment(null);
    }, [clearTimers, outstandingBalance]);

    useEffect(() => {
        if (open) {
            resetDialog();
        } else {
            clearTimers();
        }
    }, [open, resetDialog, clearTimers]);

    const handleGenerateCode = async () => {
        const payAmount = Number(amount);
        const max = Number(outstandingBalance);

        if (!Number.isFinite(payAmount) || payAmount <= 0) {
            setError("Enter a valid payment amount.");
            return;
        }
        if (Number.isFinite(max) && payAmount > max) {
            setError(`Amount cannot exceed ${formatMwk(max)}.`);
            return;
        }

        setLoading(true);
        setError("");

        try {
            const { data } = await axios.post(`${getApiBaseUrl()}/FeePaymentInitiate`, {
                feeRecordId,
                amount: payAmount,
                studentName,
                schoolId,
            });

            const expiryInMinutes = Number(data?.expiryInMinutes) || 30;
            setSession({
                timedAccountNumber: data.timedAccountNumber,
                expiryDate: data.expiryDate,
                expiryInMinutes,
                referenceNumber: data.referenceNumber,
                amount: data.amount ?? payAmount,
            });
            setCountdownSeconds(Math.max(1, Math.floor(expiryInMinutes * 60)));
            setExpired(false);
            setActiveStep(1);
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const pollStatus = useCallback(async () => {
        if (!session?.referenceNumber || expired) return;

        try {
            const { data } = await axios.get(
                `${getApiBaseUrl()}/FeePaymentStatus/${encodeURIComponent(session.referenceNumber)}`
            );

            if (data?.status === "Completed") {
                clearTimers();
                setCompletedPayment(data);
                setActiveStep(2);
                return;
            }

            if (data?.status === "Expired") {
                clearTimers();
                setExpired(true);
                setCountdownSeconds(0);
            }
        } catch {
            /* keep polling — transient network errors */
        }
    }, [session?.referenceNumber, expired, clearTimers]);

    useEffect(() => {
        if (!open || activeStep !== 1 || !session?.referenceNumber || expired) {
            return undefined;
        }

        pollStatus();
        pollRef.current = setInterval(pollStatus, POLL_MS);

        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [open, activeStep, session?.referenceNumber, expired, pollStatus]);

    useEffect(() => {
        if (!open || activeStep !== 1 || expired) {
            return undefined;
        }

        tickRef.current = setInterval(() => {
            setCountdownSeconds((prev) => {
                if (prev <= 1) {
                    clearTimers();
                    setExpired(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (tickRef.current) {
                clearInterval(tickRef.current);
                tickRef.current = null;
            }
        };
    }, [open, activeStep, expired, clearTimers]);

    const handleClose = () => {
        clearTimers();
        onClose();
    };

    const handleConfirmedClose = () => {
        if (completedPayment && onPaymentConfirmed) {
            onPaymentConfirmed(completedPayment);
        }
        handleClose();
    };

    const handleGenerateNewCode = () => {
        clearTimers();
        setSession(null);
        setExpired(false);
        setCountdownSeconds(0);
        setError("");
        setActiveStep(0);
    };

    const maxBalance = Number(outstandingBalance);
    const amountNum = Number(amount);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: dialogPaperSx }}
        >
            <DialogTitle sx={{ color: "#f0ede8", pb: 1 }}>
                Pay via OneKhusa
            </DialogTitle>

            <DialogContent>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, mt: 0.5 }}>
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel
                                sx={{
                                    "& .MuiStepLabel-label": { color: "rgba(240,237,232,0.65)" },
                                    "& .MuiStepLabel-label.Mui-active": { color: "#e8c97e" },
                                    "& .MuiStepLabel-label.Mui-completed": { color: "#e8c97e" },
                                    "& .MuiStepIcon-root": { color: "rgba(255,255,255,0.25)" },
                                    "& .MuiStepIcon-root.Mui-active": { color: "#e8c97e" },
                                    "& .MuiStepIcon-root.Mui-completed": { color: "#e8c97e" },
                                }}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {error ? (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                {activeStep === 0 && (
                    <Stack spacing={2.5}>
                        <Typography variant="body2" color="rgba(240,237,232,0.75)">
                            Generate a one-time account number for{" "}
                            <strong>{studentName}</strong>. The parent pays via mobile money or bank
                            transfer; we confirm automatically.
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            label="Amount (MWK)"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            inputProps={{
                                min: 0,
                                max: Number.isFinite(maxBalance) ? maxBalance : undefined,
                                step: 1,
                            }}
                            helperText={
                                Number.isFinite(maxBalance)
                                    ? `Outstanding balance: ${formatMwk(maxBalance)}`
                                    : undefined
                            }
                            sx={{
                                "& .MuiInputLabel-root": { color: "rgba(240,237,232,0.65)" },
                                "& .MuiOutlinedInput-root": {
                                    color: "#f0ede8",
                                    "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                                    "&:hover fieldset": { borderColor: "#e8c97e" },
                                    "&.Mui-focused fieldset": { borderColor: "#e8c97e" },
                                },
                                "& .MuiFormHelperText-root": { color: "rgba(240,237,232,0.55)" },
                            }}
                        />
                        <Button
                            variant="contained"
                            size="large"
                            disabled={loading || !amountNum || amountNum <= 0}
                            onClick={handleGenerateCode}
                            sx={goldButtonSx}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : "Generate Payment Code"}
                        </Button>
                    </Stack>
                )}

                {activeStep === 1 && session && (
                    <Stack spacing={2.5} alignItems="center" textAlign="center">
                        {!expired ? (
                            <>
                                <Typography variant="body2" color="rgba(240,237,232,0.75)">
                                    Ask the parent/guardian to send:
                                </Typography>
                                <Typography variant="h4" fontWeight={800} sx={{ color: "#e8c97e" }}>
                                    {formatMwk(session.amount)}
                                </Typography>
                                <Typography variant="body2" color="rgba(240,237,232,0.75)">
                                    To account number below via Airtel Money, TNM Mpamba, National Bank, or any bank
                                </Typography>
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        borderRadius: 2,
                                        border: "1px solid rgba(232,201,126,0.35)",
                                        bgcolor: "rgba(232,201,126,0.08)",
                                        width: "100%",
                                    }}
                                >
                                    <Typography
                                        component="div"
                                        sx={{
                                            fontSize: 48,
                                            fontWeight: 800,
                                            letterSpacing: 2,
                                            color: "#e8c97e",
                                            wordBreak: "break-all",
                                            lineHeight: 1.15,
                                        }}
                                    >
                                        {session.timedAccountNumber}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="rgba(240,237,232,0.65)">
                                    Code expires in{" "}
                                    <Box component="span" sx={{ color: "#e8c97e", fontWeight: 700 }}>
                                        {formatCountdown(countdownSeconds)}
                                    </Box>
                                </Typography>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <CircularProgress size={20} sx={{ color: "#e8c97e" }} />
                                    <Typography variant="body2" color="rgba(240,237,232,0.75)">
                                        Checking for payment…
                                    </Typography>
                                </Stack>
                            </>
                        ) : (
                            <>
                                <Alert severity="warning" sx={{ width: "100%", borderRadius: 2 }}>
                                    Payment code expired
                                </Alert>
                                <Typography variant="body2" color="rgba(240,237,232,0.75)">
                                    Generate a new code to try again.
                                </Typography>
                                <Button variant="contained" onClick={handleGenerateNewCode} sx={goldButtonSx}>
                                    Generate new code
                                </Button>
                            </>
                        )}
                    </Stack>
                )}

                {activeStep === 2 && completedPayment && (
                    <Stack spacing={2} alignItems="center" textAlign="center" py={1}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 80, color: "#4caf50" }} />
                        <Typography variant="h5" fontWeight={800} sx={{ color: "#f0ede8" }}>
                            Payment received!
                        </Typography>
                        <Typography variant="body1" color="rgba(240,237,232,0.85)">
                            Amount received: {formatMwk(completedPayment.amountReceived ?? completedPayment.amount)}
                        </Typography>
                        {completedPayment.transactionRef ? (
                            <Typography variant="body2" color="rgba(240,237,232,0.65)">
                                Transaction ref: {completedPayment.transactionRef}
                            </Typography>
                        ) : null}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                {activeStep === 2 ? (
                    <Button onClick={handleConfirmedClose} variant="contained" sx={goldButtonSx}>
                        Close
                    </Button>
                ) : (
                    <Button onClick={handleClose} sx={{ color: "rgba(240,237,232,0.75)" }}>
                        Cancel
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
